/**
 * ConversationScreen
 *
 * Full-screen centered orb conversation interface.
 * NO transcript, NO timer, NO score visible during conversation.
 * Phase shown through orb color. Subtle "End Call" button.
 *
 * Uses useVoiceSession hook for all voice session management.
 */

import { useState, useCallback, useEffect } from 'react';
import { PulsingOrb } from './PulsingOrb';
import { useVoiceSession } from '../../hooks/useVoiceSession';
import type { VapTranscriptTurn } from '../../lib/gemini';
import type { PhaseConfig, SessionPhase } from '../../lib/gemini/provider';

interface ConversationScreenProps {
  agentPrompt: string;
  assessmentId: string;
  authToken?: string;
  phaseConfig?: PhaseConfig;
  onSessionEnd: (transcript: VapTranscriptTurn[]) => void;
  onError: (error: Error) => void;
}

export function ConversationScreen({
  agentPrompt,
  assessmentId,
  authToken,
  phaseConfig,
  onSessionEnd,
  onError,
}: ConversationScreenProps) {
  const [audioLevel, setAudioLevel] = useState(0);
  const [isEnding, setIsEnding] = useState(false);

  const {
    status,
    phaseState,
    error,
    startSession,
    endSession,
  } = useVoiceSession({
    agentPrompt,
    assessmentId,
    authToken,
    phaseConfig,
  });

  // Auto-start session on mount
  useEffect(() => {
    startSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Simulate audio level from session activity
  // In production, this would come from the AudioCapture onVolumeLevel callback.
  // For now, we create a subtle pulse when session is active.
  useEffect(() => {
    if (status !== 'active') {
      setAudioLevel(0);
      return;
    }

    // Subtle simulated audio activity
    const interval = setInterval(() => {
      setAudioLevel(Math.random() * 0.3 + 0.1);
    }, 150);

    return () => clearInterval(interval);
  }, [status]);

  // Forward errors to parent
  useEffect(() => {
    if (error && status === 'error') {
      onError(new Error(error));
    }
  }, [error, status, onError]);

  const handleEndCall = useCallback(async () => {
    setIsEnding(true);
    try {
      const transcript = await endSession();
      onSessionEnd(transcript);
    } catch (err) {
      onError(err instanceof Error ? err : new Error('Failed to end session'));
    }
  }, [endSession, onSessionEnd, onError]);

  const currentPhase: SessionPhase | 'idle' = phaseState?.phase ?? 'idle';
  const isConnecting = status === 'connecting' || status === 'connected';
  const isActive = status === 'active';
  const hasError = status === 'error';

  return (
    <div className="learner-conversation">
      {/* Status text - top */}
      <div className="learner-conversation-status">
        {isConnecting && (
          <span className="learner-conversation-connecting">Connecting...</span>
        )}
        {isActive && phaseState?.phase === 'warm_up' && (
          <span className="learner-conversation-phase">Getting ready...</span>
        )}
        {isActive && phaseState?.phase === 'in_character' && (
          <span className="learner-conversation-phase">Assessment in progress</span>
        )}
        {isActive && !phaseState && (
          <span className="learner-conversation-phase">Conversation in progress</span>
        )}
      </div>

      {/* Centered orb */}
      <div className="learner-conversation-orb">
        <PulsingOrb
          isActive={isActive}
          audioLevel={isConnecting ? 0.05 : audioLevel}
          phase={isActive ? currentPhase : 'idle'}
        />
      </div>

      {/* Bottom controls */}
      <div className="learner-conversation-controls">
        {hasError && (
          <div className="learner-conversation-error">
            <p>{error ?? 'Connection error'}</p>
            <button
              className="btn btn-primary"
              onClick={() => startSession()}
            >
              Retry
            </button>
          </div>
        )}

        {(isActive || isConnecting) && !isEnding && (
          <button
            className="learner-conversation-end-btn"
            onClick={handleEndCall}
            disabled={isConnecting}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M3 8.5C3 7.12 4.12 6 5.5 6H14.5C15.88 6 17 7.12 17 8.5V11.5C17 12.88 15.88 14 14.5 14H5.5C4.12 14 3 12.88 3 11.5V8.5Z"
                fill="currentColor"
              />
            </svg>
            End Call
          </button>
        )}

        {isEnding && (
          <span className="learner-conversation-ending">Ending conversation...</span>
        )}
      </div>
    </div>
  );
}
